"use client";

import { useEffect, useMemo, useState, useRef } from "react";

import styles from "../../Stock Transfer/css/stockTransfer.module.css";
import srStyles from "../css/stockRequest.module.css";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import Button from "../../../../components/Button";
import Dropdown from "../../../../components/Dropdown";
import StockTable from "../../components/StockTable";
import SuccessNotification from "../../../../components/SuccessNotification.jsx";

import {
  fetchStockRequests,
  createStockRequest,
  fetchBranches,
  approveStockRequest,
  declineStockRequest,
} from "../../../../api/Inventory/StockRequestApi.js";

function normalizeRows(data) {
  return (data || []).map((row) => ({
    ...row,
    drugName: row.drugName ?? row["Product Name"] ?? "",
    type: row.type ?? row["Type"] ?? "",
    currentQty: row.currentQty ?? row["Current Quantity"] ?? 0,
    requestedQty: row.requestedQty ?? row["Requested Quantity"] ?? 0,
    transferTo: row.transferTo ?? row["Transfer To"] ?? "",
    transferFrom:
      row.transferFrom ?? row["Transfered from"] ?? row["Transferred from"] ?? "",
    status: row.status ?? row["Status"] ?? "Pending",
    requestDate: row.requestDate ?? row["Request Date"] ?? "",
    department: row.department ?? row["department"] ?? "",
  }));
}

// Helper: format yyyy-mm-dd to dd/mm/yyyy for UI display only
const formatDateDMY = (yyyyMmDd) => {
  const s = String(yyyyMmDd || '').trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return s;
  return `${m[3]}/${m[2]}/${m[1]}`;
};

export default function StockRequest() {
  const [mode, setMode] = useState("Requests");
  const tableRef = useRef(null);
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [activeSegment, setActiveSegment] = useState("Front Shop");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 4;
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const [data, branchRows] = await Promise.all([
          fetchStockRequests(mode),
          fetchBranches(),
        ]);
        if (!isMounted) return;
        setRows(normalizeRows(data));
        setBranches(Array.isArray(branchRows) ? branchRows : []);
      } catch (err) {
        console.error("Failed to load stock requests", err);
        setRows([]);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [mode]);

  const reloadRows = async (currentMode) => {
    const data = await fetchStockRequests(currentMode);
    setRows(normalizeRows(data));
  };

  const handleSubmitRequest = async (payload) => {
    try {
      console.log("Submitting stock request payload:", payload);
      await createStockRequest(payload);
      await reloadRows(mode);
      // Close the request modal and show global success notification
      if (tableRef.current && typeof tableRef.current.closeRequestModal === "function") {
        tableRef.current.closeRequestModal();
      }
      setShowSuccess(true);
    } catch (err) {
      console.error("Failed to submit stock request", err);
    }
  };

  const handleApprove = async (requestId) => {
    try {
      if (!requestId) return;
      await approveStockRequest(requestId);
      await reloadRows(mode);
    } catch (err) {
      console.error("Failed to approve stock request", err);
    }
  };

  const handleDecline = async (requestId, reason) => {
    try {
      if (!requestId) return;
      await declineStockRequest(requestId, reason);
      await reloadRows(mode);
    } catch (err) {
      console.error("Failed to decline stock request", err);
    }
  };

  const filteredRows = useMemo(
    () => rows.filter((r) => r.department === activeSegment),
    [rows, activeSegment]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / pageSize));

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredRows.slice(start, start + pageSize);
  }, [filteredRows, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeSegment, mode]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  return (
    <div className={styles.wrapper}>
      <Navbar />
      <div className={styles.dashboardLayout}>
        <main className={styles.main}>
          <div className={styles.container}>
            {showSuccess && (
              <SuccessNotification
                message="Stock request sent"
                onClose={() => setShowSuccess(false)}
              />
            )}
            <div className={styles.headerRow} />
            <StockTable
              rows={pagedRows}
              controlsClassName={srStyles.controlsRow}
              mode={mode}
              leftContent={
                <Dropdown
                  name="stockRequestMode"
                  options={["Requests", "Review"]}
                  value={mode}
                  onChange={setMode}
                  className={srStyles.leftControl}
                />
              }
              rightContent={
                mode === "Requests" && (
                  <Button
                    variant="primary"
                    onClick={() => tableRef.current?.openRequestModal()}
                    className={srStyles.rightControl}
                  >
                    Request Stock
                  </Button>
                )
              }
              showRightContent={mode === "Requests"}
              onSubmitRequest={handleSubmitRequest}
              onApprove={handleApprove}
              onDecline={handleDecline}
              activeSegment={activeSegment}
              onSegmentChange={setActiveSegment}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              branches={branches}
              ref={tableRef}
            />
          </div>
        </main>
      </div>
      <div className={styles.footerWrapper}>
        <Footer />
      </div>
    </div>
  );
}